// THE SIGN-OFF CERTIFICATE ON SCREEN (owner 2026-11-01: "a button instead of view model,
// it should say see sign-off certificate"; "reference the correct export id / commit /
// revision, and date/time in UTC, EST, CST"; the non-signed-off parts hidden). The same
// record as the PDF (back/utils/signoffPdf.js): what was signed, the model it refers to,
// the dates, every party with their star and role, and the views - the real model as it
// was sent for sign-off when the request kept them, else the isometric of what it covers.
import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tag } from "antd";
import { CheckOutlined, ClockCircleOutlined, DownloadOutlined, StarFilled } from "@ant-design/icons";
import { requestViewUrl } from "api/signoff";
import SignoffIsometric from "./SignoffIsometric";
import { RING, stateAt } from "./signoffState";
import { levelsFrom } from "./isoModel";
import { dayOnly, threeZones } from "./signoffTimes";
import { gridText } from "./gridSpan";

const STAR = { ownerGc: "Owner/GC", tenant: "Contractor", partner: "Trade partner" };
const ORDER = { ownerGc: 0, tenant: 1, partner: 2 };
const KINDS = [["iso", "Iso"], ["isoWest", "West iso"], ["isoEast", "East iso"], ["top", "Top"], ["section", "Section"]];
const TARGET = { system: "System", area: "Area", level: "Level", activity: "Schedule activity" };

/** The parties in the owner's order: owner/GC (blue), contractor (gold), trade partners (silver). */
export function orderedParties(parties) {
  return [...(parties || [])].filter((p) => p.name || p.email).sort((a, b) => (ORDER[a.star] ?? 3) - (ORDER[b.star] ?? 3));
}

/** "Revision #2 · export 20261031_190337_705 · commit 6ae644…" */
export function modelLine(m) {
  if (!m) return "No model export at the time";
  return [m.revision ? `Revision #${m.revision}` : "", m.exportVersion ? `export ${m.exportVersion}` : "", m.commitId ? `commit ${m.commitId}` : ""].filter(Boolean).join(" · ");
}

function Row({ k, children }) {
  return <div className="cert-row"><span>{k}</span><b>{children || "-"}</b></div>;
}

export default function SignoffCertificate({ projectId, projectTitle, ring, tiles, coveredKeys, areas = {}, levels = {}, rings = [], gridLines, onPdf, onClose }) {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    if (!ring || !ring.hasViews || !ring.requestId) { setUrls([]); return undefined; }
    let live = true;
    const made = [];
    Promise.all(KINDS.map(([kind, title]) => requestViewUrl(projectId, ring.requestId, ring.level, kind).then((u) => ({ kind, title, u }))))
      .then((rows) => { rows.forEach((r) => { if (r.u) made.push(r.u); }); if (live) setUrls(rows.filter((r) => r.u)); })
      .catch(() => {});
    return () => { live = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [projectId, ring]);
  // the drawing as it stood when this sign-off was given, with its own target picked out
  const certLevels = useMemo(() => levelsFrom((tiles && tiles.tiles) || [], levels), [tiles, levels]);
  const certStates = useMemo(() => (ring ? stateAt(rings, ring.at) : new Map()), [rings, ring]);
  const certScope = useMemo(() => {
    const t = ring && ring.target;
    if (!t) return { levelKeys: [], areaIds: [], systems: [], only: true };
    return {
      levelKeys: t.kind === "level" && t.levelKey ? [t.levelKey] : [],
      areaIds: t.areaSetId ? [String(t.areaSetId)] : [],
      systems: t.kind === "system" && t.system ? [t.system] : [],
      only: true,
    };
  }, [ring]);
  if (!ring) return null;
  const m = ring.model;
  const ink = ring.withdrawn ? RING.withdrawn : RING[ring.level];
  return (
    <Modal open width={980} onCancel={onClose} footer={null} title="Sign-off certificate" className="signoff-certificate" destroyOnClose>
      <div className="cert-head">
        <small className="signoff-muted">BidLight · {projectTitle || "Project"}</small>
        <h3 style={{ color: ink }}>
          <StarFilled /> {ring.withdrawn ? "Withdrawn · " : ""}{STAR[ring.level] || ""} · {ring.target ? ring.target.label : ""}
        </h3>
      </div>
      <div className="cert-grid">
        <Row k="Sign-off date">{ring.agreedAt ? `${dayOnly(ring.agreedAt)} (agreed)` : dayOnly(ring.at)}</Row>
        <Row k="Signed">{threeZones(ring.signedAt || ring.at)}</Row>
        {ring.requiredBy ? <Row k="Required by">{dayOnly(ring.requiredBy)}</Row> : null}
        {ring.phase ? <Row k="Phase">{ring.phase}</Row> : null}
        <Row k="Target">{`${TARGET[ring.target && ring.target.kind] || "Target"} · ${ring.target ? ring.target.label : ""}`}</Row>
        <Row k="Model">{modelLine(m)}</Row>
        {m && m.exportedAt ? <Row k="Exported">{threeZones(m.exportedAt)}</Row> : null}
        <Row k="Covers">{`${(coveredKeys || []).length} tiles`}</Row>
        <Row k="Building grid">{gridText(gridLines, tiles && tiles.grid, coveredKeys)}</Row>
      </div>
      <div className="signoff-box-title mt-3">{ring.withdrawn ? "Withdrawn by" : "Parties"}</div>
      {ring.withdrawn ? <div className="cert-party">{ring.byName}</div> : orderedParties(ring.parties).map((p, i) => (
        <div key={i} className={`cert-party ${p.signed ? "is-signed" : ""}`} data-testid="cert-party">
          <StarFilled style={{ color: RING[p.star] || "#c7ccd4" }} />{" "}
          {p.signed ? <CheckOutlined /> : <ClockCircleOutlined />} <b>{p.name || p.email}</b>
          {p.company ? ` · ${p.company}` : ""} · {STAR[p.star] || p.role}{p.title ? ` · ${p.title}` : ""}
          {p.signed ? <span className="signoff-muted"> · {threeZones(p.at)}</span> : null}
          {p.pending ? <Tag color="orange" className="ml-1">pending</Tag> : null}
          {p.outcome === "changes" ? <Tag color="orange" className="ml-1">asked for changes</Tag> : null}
        </div>
      ))}
      <div className="signoff-box-title mt-3">
        {urls.length ? "The signed-off elements, as sent for sign-off" : "What this sign-off covers"}
      </div>
      {urls.length ? (
        <div className="cert-views">
          {urls.map((v) => <figure key={v.kind}><img src={v.u} alt={v.title} /><figcaption>{v.title}</figcaption></figure>)}
        </div>
      ) : tiles && certLevels.length ? (
        <SignoffIsometric tiles={tiles} levels={certLevels} areas={areas || {}} manifest={null}
                          states={certStates} scope={certScope} focusKeys={coveredKeys} height={380} />
      ) : null}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="signoff-muted">Reference {ring.id}</small>
        {onPdf && ring.type === "signoff" ? <Button icon={<DownloadOutlined />} onClick={() => onPdf(ring)}>Certificate PDF</Button> : null}
      </div>
    </Modal>
  );
}
